import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : 'https://api.daggpt.network';
const ADMIN_TOKEN = process.env.REACT_APP_ADMIN_SECRET_KEY || '';

const nm = {
  bg: '#e8ecf1',
  shadow: '8px 8px 16px #c5c9cd, -8px -8px 16px #ffffff',
  shadowSm: '4px 4px 8px #c5c9cd, -4px -4px 8px #ffffff',
  shadowInset: 'inset 4px 4px 8px #c5c9cd, inset -4px -4px 8px #ffffff',
};

const adminHeaders = { 'Content-Type': 'application/json', 'x-admin-token': ADMIN_TOKEN };
const fmt = (n) => (+(n ?? 0)).toLocaleString(undefined, { maximumFractionDigits: 2 });
const usd = (n) => `$${(+(n ?? 0)).toFixed(2)}`;

const TYPE_COLORS = {
  purchase:      '#7c3aed',
  bonus:         '#8b5cf6',
  deduction:     '#6d28d9',
  refund:        '#a78bfa',
  adjustment:    '#5b21b6',
  dgcc_transfer: '#4c1d95',
  transfer:      '#4c1d95',
};

function NmButton({ children, onClick, active, style = {} }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
      fontSize: 11, fontWeight: 700, transition: 'all 0.15s',
      background: active ? 'linear-gradient(135deg,#7c3aed,#6d28d9)' : nm.bg,
      color: active ? '#fff' : '#64748b',
      boxShadow: active ? '0 3px 10px rgba(124,58,237,0.3)' : nm.shadowSm,
      ...style,
    }}>{children}</button>
  );
}

function Paginator({ page, total, limit, onChange }) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (totalPages <= 1) return null;
  // Show window of pages around current
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, page + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
      <NmButton onClick={() => onChange(1)} active={false} style={{ opacity: page === 1 ? 0.4 : 1 }}>«</NmButton>
      <NmButton onClick={() => onChange(Math.max(1, page - 1))} active={false} style={{ opacity: page === 1 ? 0.4 : 1 }}>‹</NmButton>
      {start > 1 && <span style={{ color: '#94a3b8', fontSize: 12 }}>…</span>}
      {pages.map(p => <NmButton key={p} onClick={() => onChange(p)} active={p === page}>{p}</NmButton>)}
      {end < totalPages && <span style={{ color: '#94a3b8', fontSize: 12 }}>…</span>}
      <NmButton onClick={() => onChange(Math.min(totalPages, page + 1))} active={false} style={{ opacity: page === totalPages ? 0.4 : 1 }}>›</NmButton>
      <NmButton onClick={() => onChange(totalPages)} active={false} style={{ opacity: page === totalPages ? 0.4 : 1 }}>»</NmButton>
      <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 6 }}>Page {page} of {totalPages} · {fmt(total)} total</span>
    </div>
  );
}

// ─── Users View ──────────────────────────────────────────────────
export function AdminUsersView() {
  const [users,        setUsers]        = useState([]);
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [page,         setPage]         = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [adjusting,    setAdjusting]    = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [notification, setNotification] = useState(null);

  useEffect(() => { loadUsers(); }, [page, search]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_URL}/api/admin/users?page=${page}&limit=20&search=${encodeURIComponent(search)}`, { headers: adminHeaders });
      const data = await res.json();
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadUser = async (userId) => {
    try {
      const res  = await fetch(`${API_URL}/api/admin/users/${userId}`, { headers: adminHeaders });
      const data = await res.json();
      setSelectedUser(data);
    } catch (e) { console.error(e); }
  };

  const handleAdjust = async () => {
    if (!adjustAmount || !selectedUser) return;
    try {
      const res  = await fetch(`${API_URL}/api/admin/users/${selectedUser.user?.id}/adjust-credits`, {
        method: 'POST', headers: adminHeaders,
        body: JSON.stringify({ amount: parseInt(adjustAmount), reason: adjustReason || 'Admin adjustment' }),
      });
      const data = await res.json();
      if (data.success) {
        showNotification(`Adjusted ${adjustAmount} DGCC. New balance: ${fmt(data.newBalance)}`, 'success');
        setAdjusting(false); setAdjustAmount(''); setAdjustReason('');
        loadUser(selectedUser.user?.id); loadUsers();
      }
    } catch { showNotification('Failed to adjust credits', 'error'); }
  };

  const showNotification = (msg, type) => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 4000);
  };

  return (
    <div style={{ display: 'flex', gap: 24, minHeight: '70vh' }}>
      {/* Users list */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Search */}
        <div style={{ background: nm.bg, borderRadius: 12, padding: '10px 16px', boxShadow: nm.shadowInset, display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name, email or wallet..."
            style={{ background: 'transparent', border: 'none', outline: 'none', flex: 1, fontSize: 13, color: '#0f172a' }} />
        </div>

        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{fmt(total)} users total · page {page}</div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>Loading users...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {users.map(user => {
              const credits = user.credits;
              return (
                <div key={user.id} onClick={() => loadUser(user.id)}
                  style={{
                    background: nm.bg, borderRadius: 12, padding: '12px 16px', boxShadow: nm.shadowSm, cursor: 'pointer',
                    border: `2px solid ${selectedUser?.user?.id === user.id ? '#7c3aed' : 'transparent'}`,
                    transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 15, fontWeight: 800 }}>
                    {(user.full_name || user.email || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.full_name || 'Unnamed User'}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email || user.wallet_address?.slice(0, 20) + '...'}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed' }}>{fmt(credits?.balance || 0)} DGCC</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{user.role}</div>
                  </div>
                </div>
              );
            })}
            {users.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: 32 }}>No users found</div>}
          </div>
        )}

        {/* Pagination */}
        <Paginator page={page} total={total} limit={20} onChange={p => setPage(p)} />
      </div>

      {/* User detail panel */}
      <AnimatePresence>
        {selectedUser && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
            style={{ width: 380, background: nm.bg, borderRadius: 16, padding: 24, boxShadow: nm.shadow, display: 'flex', flexDirection: 'column', gap: 18, overflowY: 'auto', maxHeight: '80vh' }}>

            {/* Profile */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, fontWeight: 800, flexShrink: 0 }}>
                {(selectedUser.user?.full_name || '?')[0].toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{selectedUser.user?.full_name || 'Unnamed'}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{selectedUser.user?.email}</div>
                {selectedUser.user?.wallet_address && (
                  <div style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>{selectedUser.user.wallet_address.slice(0, 18)}...</div>
                )}
              </div>
            </div>

            {/* Credit summary */}
            <div style={{ background: nm.bg, borderRadius: 12, padding: 16, boxShadow: nm.shadowInset }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>DGCC Coins</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  ['Balance',   selectedUser.credits?.balance],
                  ['Purchased', selectedUser.credits?.total_purchased],
                  ['Spent',     selectedUser.credits?.total_spent],
                  ['Bonus',     selectedUser.credits?.total_bonus],
                ].map(([label, val]) => (
                  <div key={label}>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#7c3aed' }}>{fmt(val)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Adjust credits */}
            {!adjusting ? (
              <button onClick={() => setAdjusting(true)} style={{ width: '100%', padding: '10px', borderRadius: 10, border: 'none', background: nm.bg, color: '#7c3aed', fontWeight: 700, fontSize: 13, boxShadow: nm.shadowSm, cursor: 'pointer' }}>
                Adjust DGCC Balance
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input type="number" value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)}
                  placeholder="Amount (negative to deduct)"
                  style={{ padding: '10px 14px', borderRadius: 10, border: 'none', background: nm.bg, boxShadow: nm.shadowInset, fontSize: 13, color: '#0f172a', outline: 'none' }} />
                <input value={adjustReason} onChange={e => setAdjustReason(e.target.value)}
                  placeholder="Reason (e.g. Compensation, Error fix)"
                  style={{ padding: '10px 14px', borderRadius: 10, border: 'none', background: nm.bg, boxShadow: nm.shadowInset, fontSize: 13, color: '#0f172a', outline: 'none' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={handleAdjust} style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', boxShadow: '0 4px 12px rgba(124,58,237,0.3)' }}>Apply</button>
                  <button onClick={() => setAdjusting(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: nm.bg, boxShadow: nm.shadowSm, color: '#64748b', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Transaction history */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Recent Transactions</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {(selectedUser.transactions || []).slice(0, 10).map(tx => (
                  <div key={tx.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: nm.bg, borderRadius: 8, boxShadow: nm.shadowInset }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{tx.description || tx.type}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>{new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: TYPE_COLORS[tx.type] || '#64748b' }}>
                      {tx.amount > 0 ? '+' : ''}{fmt(tx.amount)}
                    </div>
                  </div>
                ))}
                {(!selectedUser.transactions || selectedUser.transactions.length === 0) && (
                  <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, padding: '16px 0' }}>No transactions</div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {notification && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }}
            style={{ position: 'fixed', bottom: 24, right: 24, padding: '14px 20px', borderRadius: 12, background: notification.type === 'success' ? '#ecfdf5' : '#fef2f2', color: notification.type === 'success' ? '#065f46' : '#991b1b', fontWeight: 700, fontSize: 13, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 9999 }}>
            {notification.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Billing Overview View ────────────────────────────────────────
export function AdminBillingView() {
  const [summary,      setSummary]      = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [txLoading,    setTxLoading]    = useState(false);
  const [txFilter,     setTxFilter]     = useState('all');
  const [txPage,       setTxPage]       = useState(1);
  const [txTotal,      setTxTotal]      = useState(0);

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadTransactions(); }, [txFilter, txPage]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/billing/summary`, { headers: adminHeaders });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSummary(data);
    } catch (e) { console.error('Billing summary error:', e); setSummary({}); }
    finally { setLoading(false); }
  };

  const loadTransactions = async () => {
    setTxLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/billing/transactions?page=${txPage}&type=${txFilter}&limit=20`, { headers: adminHeaders });
      const data = await res.json();
      const txList = data?.transactions;
      setTransactions(Array.isArray(txList) ? txList : []);
      setTxTotal(typeof data?.total === 'number' ? data.total : 0);
    } catch (e) { console.error(e); setTransactions([]); }
    finally { setTxLoading(false); }
  };

  const s = summary || {};
  const trend = Array.isArray(s.trend) ? s.trend : [];
  const maxGen = Math.max(...trend.map(d => d.gens), 1);
  const typeAccent = { chat: '#7c3aed', image: '#8b5cf6', video: '#6d28d9', music: '#a78bfa', website: '#5b21b6' };
  const byType = s.byGenerationType && typeof s.byGenerationType === 'object' && !Array.isArray(s.byGenerationType) ? Object.entries(s.byGenerationType) : [];
  const topModels = Array.isArray(s.topModels) ? s.topModels : [];

  const KpiCard = ({ label, value, sub, accent = '#7c3aed', highlight = false }) => (
    <div style={{ background: nm.bg, borderRadius: 16, padding: '18px 20px', boxShadow: highlight ? nm.shadow : nm.shadowSm, border: highlight ? '1px solid rgba(124,58,237,0.15)' : 'none' }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 900, color: accent, letterSpacing: '-0.5px' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, fontWeight: 500 }}>{sub}</div>}
    </div>
  );

  const SectionTitle = ({ children, right }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{children}</div>
      {right && <div style={{ fontSize: 11, color: '#94a3b8' }}>{right}</div>}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 13, fontWeight: 600 }}>Loading billing analytics...</div>
      ) : (
        <>
          {/* Row 1: Primary KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12 }}>
            <KpiCard label="Total Revenue"    value={usd(s.totalRevenue)}          sub={`Net: ${usd(s.netRevenue)}`}                      accent="#7c3aed" highlight />
            <KpiCard label="This Month"       value={usd(s.monthRevenue)}          sub={`Last 7d: ${usd(s.week7Revenue)}`}                accent="#6d28d9" />
            <KpiCard label="Gross Profit"     value={usd(s.totalProfitUsd)}        sub={`${s.profitMarginPct ?? 0}% net margin`}          accent="#5b21b6" highlight />
            <KpiCard label="Markup %"         value={`${s.markupPercent ?? 0}%`}   sub={`On ${usd(s.totalAggregatorCostUsd)} agg. cost`}  accent="#4c1d95" />
            <KpiCard label="Refunded"         value={usd(s.totalRefunded)}         sub="Stripe refunds"                                   accent="#94a3b8" />
            <KpiCard label="DGCC Consumed"    value={fmt(s.totalDgccConsumed)}     sub={`${fmt(s.totalCreditsConsumed)} generations`}     accent="#8b5cf6" />
          </div>

          {/* Row 2: DGCC Economy & User KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12 }}>
            <KpiCard label="Total Users"       value={fmt(s.totalUsers)}             sub={`${fmt(s.payingUsers)} paying`}             accent="#7c3aed" />
            <KpiCard label="ARPU"              value={usd(s.arpu)}                   sub="Avg revenue / paying user"                 accent="#6d28d9" />
            <KpiCard label="DGCC Purchased"    value={fmt(s.totalDgccPurchased)}     sub="All time by users"                         accent="#5b21b6" />
            <KpiCard label="DGCC Circulating"  value={fmt(s.totalDgccInCirculation)} sub="Sum of all balances"                       accent="#4c1d95" />
            <KpiCard label="DGCC Bonuses"      value={fmt(s.totalDgccBonus)}         sub="Granted to users"                          accent="#7c3aed" />
            <KpiCard label="DGCC Transferred"  value={fmt(s.totalDgccTransferred)}   sub="Cross-platform moves"                      accent="#6d28d9" />
          </div>

          {/* Row 3: Provider Split + Trend Chart */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>

            <div style={{ background: nm.bg, borderRadius: 16, padding: 22, boxShadow: nm.shadowSm }}>
              <SectionTitle>OpenRouter - Text and Code</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  ['Aggregator Cost', `$${(s.openRouterCost||0).toFixed(4)}`],
                  ['Profit',          `$${(s.openRouterProfit||0).toFixed(4)}`],
                  ['Generations',     fmt(s.openRouterCount)],
                  ['Avg Cost / Gen',  s.openRouterCount > 0 ? `$${((s.openRouterCost||0)/s.openRouterCount).toFixed(5)}` : '-'],
                ].map(([lbl, val]) => (
                  <div key={lbl}>
                    <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>{lbl}</div>
                    <div style={{ fontSize: 17, fontWeight: 900, color: '#7c3aed' }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: nm.bg, borderRadius: 16, padding: 22, boxShadow: nm.shadowSm }}>
              <SectionTitle>Kei.ai - Image · Video · Music</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  ['Aggregator Cost', `$${(s.keiAiCost||0).toFixed(4)}`],
                  ['Profit',          `$${(s.keiAiProfit||0).toFixed(4)}`],
                  ['Generations',     fmt(s.keiAiCount)],
                  ['Avg Cost / Gen',  s.keiAiCount > 0 ? `$${((s.keiAiCost||0)/s.keiAiCount).toFixed(5)}` : '-'],
                ].map(([lbl, val]) => (
                  <div key={lbl}>
                    <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>{lbl}</div>
                    <div style={{ fontSize: 17, fontWeight: 900, color: '#7c3aed' }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: nm.bg, borderRadius: 16, padding: 22, boxShadow: nm.shadowSm }}>
              <SectionTitle right="last 7 days">Daily Activity</SectionTitle>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 64, marginBottom: 6 }}>
                {trend.map((d, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ width: '100%', background: `rgba(124,58,237,${0.2 + 0.8*(d.gens/maxGen)})`, borderRadius: '3px 3px 0 0', height: `${Math.max(4, (d.gens/maxGen)*60)}px` }} />
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
                {trend.map((d, i) => (
                  <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>
                    {new Date(d.date).toLocaleDateString('en-US',{weekday:'short'}).slice(0,2)}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>7d Gens</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#7c3aed' }}>{fmt(trend.reduce((a,d)=>a+d.gens,0))}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>7d Revenue</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#5b21b6' }}>{usd(trend.reduce((a,d)=>a+d.revenue,0))}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 4: Generation Type + Top Models */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ background: nm.bg, borderRadius: 16, padding: 22, boxShadow: nm.shadowSm }}>
              <SectionTitle>Revenue by Generation Type</SectionTitle>
              {byType.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0', fontSize: 12 }}>No generation data yet</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f5f3ff' }}>
                      {['Type','Gens','DGCC','Agg. Cost','Revenue','Profit','Margin'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '7px 8px', fontSize: 9, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid rgba(124,58,237,0.1)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {byType.map(([type, st]) => (
                      <tr key={type} style={{ borderBottom: '1px solid #f1f5f9' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f5f3ff'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '8px 8px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: typeAccent[type] || '#94a3b8', flexShrink: 0 }} />
                            <span style={{ fontWeight: 700, color: '#0f172a' }}>{type}</span>
                          </span>
                        </td>
                        <td style={{ padding: '8px 8px', color: '#64748b', fontWeight: 600 }}>{fmt(st?.count)}</td>
                        <td style={{ padding: '8px 8px', color: '#7c3aed', fontWeight: 700 }}>{fmt(st?.dgcc)}</td>
                        <td style={{ padding: '8px 8px', color: '#64748b' }}>${(st?.aggCost||0).toFixed(3)}</td>
                        <td style={{ padding: '8px 8px', color: '#5b21b6', fontWeight: 700 }}>${(st?.charged||0).toFixed(3)}</td>
                        <td style={{ padding: '8px 8px', color: '#6d28d9', fontWeight: 700 }}>${(st?.profit||0).toFixed(3)}</td>
                        <td style={{ padding: '8px 8px' }}>
                          <span style={{ background: 'rgba(124,58,237,0.1)', color: '#7c3aed', padding: '2px 7px', borderRadius: 5, fontWeight: 800, fontSize: 10 }}>{st?.margin ?? 0}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ background: nm.bg, borderRadius: 16, padding: 22, boxShadow: nm.shadowSm }}>
              <SectionTitle right="by usage count">Top AI Models</SectionTitle>
              {topModels.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0', fontSize: 12 }}>No model data yet</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f5f3ff' }}>
                      {['Model','Gens','DGCC','Revenue','Profit'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '7px 8px', fontSize: 9, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid rgba(124,58,237,0.1)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {topModels.map((m, i) => (
                      <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f5f3ff'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '8px 8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 18, height: 18, borderRadius: 5, background: `rgba(124,58,237,${Math.max(0.25, 1-i*0.1)})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{i+1}</span>
                            <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#0f172a', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>{m.id}</span>
                          </div>
                        </td>
                        <td style={{ padding: '8px 8px', color: '#7c3aed', fontWeight: 700 }}>{fmt(m.count)}</td>
                        <td style={{ padding: '8px 8px', color: '#64748b' }}>{fmt(m.dgcc)}</td>
                        <td style={{ padding: '8px 8px', color: '#5b21b6', fontWeight: 700 }}>${(m.revenue||0).toFixed(4)}</td>
                        <td style={{ padding: '8px 8px', color: '#6d28d9', fontWeight: 700 }}>${(m.profit||0).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* Financial Transaction Log */}
      <div style={{ background: nm.bg, borderRadius: 16, padding: 22, boxShadow: nm.shadowSm }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>Financial Log · {fmt(txTotal)} entries</div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {['all','purchase','deduction','bonus','refund','adjustment','dgcc_transfer'].map(f => (
              <NmButton key={f} active={txFilter === f} onClick={() => { setTxFilter(f); setTxPage(1); }}>{f}</NmButton>
            ))}
          </div>
        </div>
        {txLoading ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>Loading...</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f5f3ff' }}>
                    {['Date','User','Type','DGCC','Agg. Cost','Charged USD','Profit','Model'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '9px 12px', color: '#6b7280', fontWeight: 800, fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid rgba(124,58,237,0.1)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(transactions) ? transactions : []).map(tx => (
                    <tr key={tx.id} style={{ borderBottom: '1px solid #f1f5f9' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f5f3ff'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '9px 12px', color: '#64748b', whiteSpace: 'nowrap', fontSize: 11 }}>
                        {new Date(tx.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}
                      </td>
                      <td style={{ padding: '9px 12px', color: '#0f172a', fontWeight: 600, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {tx.users?.full_name || tx.users?.email || '-'}
                      </td>
                      <td style={{ padding: '9px 12px' }}>
                        <span style={{ background: `${TYPE_COLORS[tx.type]||'#94a3b8'}18`, color: TYPE_COLORS[tx.type]||'#64748b', padding: '2px 8px', borderRadius: 6, fontWeight: 700, fontSize: 10 }}>
                          {tx.type}
                        </span>
                      </td>
                      <td style={{ padding: '9px 12px', fontWeight: 700, color: tx.amount > 0 ? '#7c3aed' : '#6d28d9', whiteSpace: 'nowrap' }}>
                        {tx.amount > 0 ? '+' : ''}{fmt(tx.amount)}
                      </td>
                      <td style={{ padding: '9px 12px', color: '#64748b' }}>{tx.aggregator_cost_usd ? `$${Number(tx.aggregator_cost_usd).toFixed(4)}` : '-'}</td>
                      <td style={{ padding: '9px 12px', color: '#7c3aed', fontWeight: 700 }}>{tx.charged_usd ? `$${Number(tx.charged_usd).toFixed(4)}` : '-'}</td>
                      <td style={{ padding: '9px 12px', color: '#5b21b6', fontWeight: 700 }}>{tx.profit_usd ? `$${Number(tx.profit_usd).toFixed(4)}` : '-'}</td>
                      <td style={{ padding: '9px 12px', color: '#94a3b8', fontFamily: 'monospace', fontSize: 10 }}>{tx.model_id || '-'}</td>
                    </tr>
                  ))}
                  {transactions.length === 0 && (
                    <tr><td colSpan={8} style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>No transactions found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 12 }}>
              <Paginator page={txPage} total={txTotal} limit={20} onChange={p => setTxPage(p)} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Activity Logs View ───────────────────────────────────────────


// ─── Type metadata ────────────────────────────────────────────────────────────
const TYPE_META = {
  purchase:      { color: '#7c3aed', bg: '#f5f3ff', label: 'Purchase',      icon: '💳' },
  bonus:         { color: '#059669', bg: '#ecfdf5', label: 'Bonus',         icon: '🎁' },
  deduction:     { color: '#dc2626', bg: '#fef2f2', label: 'Deduction',     icon: '⚡' },
  refund:        { color: '#0891b2', bg: '#ecfeff', label: 'Refund',        icon: '↩️' },
  adjustment:    { color: '#d97706', bg: '#fffbeb', label: 'Adjustment',    icon: '⚙️' },
  dgcc_transfer: { color: '#7c3aed', bg: '#f5f3ff', label: 'DGCC Transfer', icon: '🔄' },
  transfer:      { color: '#7c3aed', bg: '#f5f3ff', label: 'Transfer',      icon: '🔄' },
};
const TYPES = ['all', 'deduction', 'purchase', 'bonus', 'refund', 'adjustment', 'dgcc_transfer', 'transfer'];

function fmtDgcc(n) {
  const v = parseFloat(n ?? 0);
  if (Math.abs(v) >= 1) return v.toFixed(4);
  if (Math.abs(v) >= 0.0001) return v.toFixed(6);
  return v.toFixed(8);
}

function fmtDateTime(iso) {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    full: d.toISOString().replace('T', ' ').slice(0, 23) + ' UTC',
  };
}

function LogTypeBadge({ type }) {
  const m = TYPE_META[type] || { color: '#64748b', bg: '#f8fafc', label: type, icon: '•' };
  return (
    <span style={{
      background: m.bg, color: m.color,
      padding: '3px 9px', borderRadius: 8,
      fontWeight: 800, fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.8,
      display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
      border: `1px solid ${m.color}28`,
    }}>
      <span>{m.icon}</span> {m.label}
    </span>
  );
}

function StatPill({ label, value, color = '#7c3aed' }) {
  return (
    <div style={{
      background: nm.bg, borderRadius: 12, padding: '10px 18px',
      boxShadow: nm.shadowSm, textAlign: 'center', minWidth: 110,
    }}>
      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 900, color }}>{value}</div>
    </div>
  );
}

export function AdminLogsView() {
  const [logs,        setLogs]        = useState([]);
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(true);
  const [page,        setPage]        = useState(1);
  const [type,        setType]        = useState('all');
  const [search,      setSearch]      = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [userFilter,  setUserFilter]  = useState('');
  const [userInput,   setUserInput]   = useState('');
  const [fromDate,    setFromDate]    = useState('');
  const [toDate,      setToDate]      = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [expanded,    setExpanded]    = useState(null); // expanded row id

  // Computed stats over current page
  const stats = logs.reduce((acc, l) => {
    const amt = parseFloat(l.amount || 0);
    if (l.type === 'deduction')  acc.totalDeducted  += Math.abs(amt);
    if (l.type === 'purchase')   acc.totalPurchased += amt;
    if (l.type === 'bonus')      acc.totalBonus     += amt;
    if (l.type === 'refund')     acc.totalRefunded  += Math.abs(amt);
    return acc;
  }, { totalDeducted: 0, totalPurchased: 0, totalBonus: 0, totalRefunded: 0 });

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page, limit: 50, type, search: search || '',
        ...(userFilter ? { user_id: userFilter } : {}),
        ...(fromDate   ? { from: fromDate + 'T00:00:00Z' } : {}),
        ...(toDate     ? { to:   toDate   + 'T23:59:59Z' } : {}),
      });
      const res  = await fetch(`${API_URL}/api/admin/logs?${params}`, { headers: adminHeaders });
      const data = await res.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setLastRefresh(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, type, search, userFilter, fromDate, toDate]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  // Auto-refresh every 15 seconds when enabled
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(loadLogs, 15000);
    return () => clearInterval(id);
  }, [autoRefresh, loadLogs]);

  // CSV export - current filtered results
  const exportCSV = () => {
    const headers = ['Timestamp (UTC)', 'User Email', 'User Name', 'Type', 'Amount (DGCC)', 'Balance After', 'Description', 'Task ID'];
    const rows = logs.map(l => [
      fmtDateTime(l.created_at).full,
      l.user?.email || '',
      l.user?.full_name || '',
      l.type,
      l.amount,
      l.balance_after,
      `"${(l.description || '').replace(/"/g, "'")}"`,
      l.task_id || '',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `daggpt-logs-${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(total / 50);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', margin: 0 }}>Activity Logs</h2>
          <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0', fontWeight: 500 }}>
            Complete audit trail - every credit transaction across all users
            {lastRefresh && <span style={{ marginLeft: 8 }}>· Last refreshed {lastRefresh.toLocaleTimeString()}</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Auto-refresh toggle */}
          <button
            onClick={() => setAutoRefresh(v => !v)}
            style={{
              background: autoRefresh ? '#7c3aed' : nm.bg,
              color: autoRefresh ? '#fff' : '#64748b',
              border: 'none', borderRadius: 10, padding: '8px 14px',
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
              boxShadow: autoRefresh ? '0 2px 8px #7c3aed44' : nm.shadowSm,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: autoRefresh ? '#4ade80' : '#94a3b8', animation: autoRefresh ? 'pulse 1.5s infinite' : 'none' }} />
            {autoRefresh ? 'Live · 15s' : 'Auto-Refresh Off'}
          </button>
          {/* Manual refresh */}
          <button onClick={loadLogs} disabled={loading} style={{ background: nm.bg, border: 'none', borderRadius: 10, padding: '8px 14px', fontSize: 11, fontWeight: 700, color: '#7c3aed', cursor: 'pointer', boxShadow: nm.shadowSm }}>
            {loading ? '⟳ Loading…' : '⟳ Refresh'}
          </button>
          {/* CSV Export */}
          <button onClick={exportCSV} disabled={logs.length === 0} style={{ background: '#7c3aed', border: 'none', borderRadius: 10, padding: '8px 16px', fontSize: 11, fontWeight: 700, color: '#fff', cursor: 'pointer', boxShadow: '0 2px 8px #7c3aed44', opacity: logs.length === 0 ? 0.4 : 1 }}>
            ↓ Export CSV
          </button>
        </div>
      </div>

      {/* ── Stats bar ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <StatPill label="Total Entries"    value={total.toLocaleString()}                          color="#0f172a" />
        <StatPill label="Deducted (page)"  value={`${fmtDgcc(stats.totalDeducted)} DGCC`}          color="#dc2626" />
        <StatPill label="Purchased (page)" value={`${fmtDgcc(stats.totalPurchased)} DGCC`}         color="#7c3aed" />
        <StatPill label="Bonuses (page)"   value={`${fmtDgcc(stats.totalBonus)} DGCC`}             color="#059669" />
        <StatPill label="Refunded (page)"  value={`${fmtDgcc(stats.totalRefunded)} DGCC`}          color="#0891b2" />
      </div>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {/* Search by description / task ID */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: nm.bg, borderRadius: 10, padding: '8px 14px', boxShadow: nm.shadowInset, flex: '1 1 200px', minWidth: 200 }}>
          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }}
            placeholder="Search description, task ID… (Enter)"
            style={{ background: 'transparent', border: 'none', outline: 'none', flex: 1, fontSize: 12, color: '#0f172a' }}
          />
          {searchInput && <button onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, fontSize: 14 }}>×</button>}
        </div>

        {/* Filter by user email/id */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: nm.bg, borderRadius: 10, padding: '8px 14px', boxShadow: nm.shadowInset, flex: '1 1 180px', minWidth: 180 }}>
          <span style={{ fontSize: 12 }}>👤</span>
          <input
            value={userInput}
            onChange={e => setUserInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setUserFilter(userInput.trim()); setPage(1); } }}
            placeholder="Filter by user ID (Enter)"
            style={{ background: 'transparent', border: 'none', outline: 'none', flex: 1, fontSize: 12, color: '#0f172a' }}
          />
          {userInput && <button onClick={() => { setUserInput(''); setUserFilter(''); setPage(1); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, fontSize: 14 }}>×</button>}
        </div>

        {/* Date range */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: nm.bg, borderRadius: 10, padding: '8px 14px', boxShadow: nm.shadowInset }}>
          <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>FROM</span>
          <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(1); }}
            style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 11, color: '#0f172a', cursor: 'pointer' }} />
          <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>TO</span>
          <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(1); }}
            style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 11, color: '#0f172a', cursor: 'pointer' }} />
          {(fromDate || toDate) && <button onClick={() => { setFromDate(''); setToDate(''); setPage(1); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, padding: 0 }}>×</button>}
        </div>
      </div>

      {/* ── Type filter pills ──────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {TYPES.map(t => {
          const m = TYPE_META[t] || {};
          const isActive = type === t;
          return (
            <button key={t} onClick={() => { setType(t); setPage(1); }} style={{
              background: isActive ? (m.bg || '#f5f3ff') : nm.bg,
              color: isActive ? (m.color || '#7c3aed') : '#64748b',
              border: isActive ? `1.5px solid ${m.color || '#7c3aed'}40` : '1.5px solid transparent',
              borderRadius: 8, padding: '5px 12px',
              fontSize: 10, fontWeight: 800, cursor: 'pointer',
              boxShadow: isActive ? `0 2px 8px ${m.color || '#7c3aed'}22` : nm.shadowSm,
              textTransform: 'uppercase', letterSpacing: 0.5,
              transition: 'all 0.15s',
            }}>
              {m.icon ? `${m.icon} ` : ''}{t === 'all' ? 'All Types' : (m.label || t)}
            </button>
          );
        })}
      </div>

      {/* ── Entry count ────────────────────────────────────────────────────── */}
      <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
        {total.toLocaleString()} entries · page {page} of {totalPages || 1}
        {(search || type !== 'all' || userFilter || fromDate || toDate) && (
          <button onClick={() => { setSearch(''); setSearchInput(''); setType('all'); setUserFilter(''); setUserInput(''); setFromDate(''); setToDate(''); setPage(1); }}
            style={{ marginLeft: 12, fontSize: 10, color: '#7c3aed', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
            Clear all filters
          </button>
        )}
      </div>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      <div style={{ background: nm.bg, borderRadius: 16, overflow: 'hidden', boxShadow: nm.shadowSm }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' }}>
                {['', 'Timestamp', 'User', 'Type', 'Amount (DGCC)', 'Balance After', 'Description', 'Task / Reference'].map((h, i) => (
                  <th key={i} style={{
                    textAlign: 'left', padding: '12px 14px',
                    color: '#6b7280', fontWeight: 800, fontSize: 9,
                    textTransform: 'uppercase', letterSpacing: 1,
                    borderBottom: '2px solid rgba(124,58,237,0.12)',
                    whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ padding: '60px', textAlign: 'center' }}>
                  <div style={{ color: '#7c3aed', fontSize: 13, fontWeight: 600 }}>⟳ Loading logs…</div>
                </td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: '60px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>
                  No activity logs match your filters
                </td></tr>
              ) : logs.map((log, idx) => {
                const dt = fmtDateTime(log.created_at);
                const isExpanded = expanded === log.id;
                const amt = parseFloat(log.amount || 0);
                const amtColor = amt > 0 ? '#059669' : amt < 0 ? '#dc2626' : '#64748b';

                return (
                  <React.Fragment key={log.id}>
                    <tr
                      onClick={() => setExpanded(isExpanded ? null : log.id)}
                      style={{
                        borderBottom: isExpanded ? 'none' : '1px solid #f1f5f9',
                        background: isExpanded ? '#faf8ff' : idx % 2 === 0 ? 'transparent' : '#fafbfc',
                        cursor: 'pointer', transition: 'background 0.12s',
                      }}
                      onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = '#f5f3ff'; }}
                      onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : '#fafbfc'; }}
                    >
                      {/* Expand chevron */}
                      <td style={{ padding: '10px 6px 10px 14px', color: '#94a3b8', fontSize: 10 }}>
                        {isExpanded ? '▾' : '▸'}
                      </td>
                      {/* Timestamp */}
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#0f172a' }}>{dt.date}</div>
                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{dt.time}</div>
                      </td>
                      {/* User */}
                      <td style={{ padding: '10px 14px', maxWidth: 150 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.user?.full_name || log.user?.email?.split('@')[0] || <span style={{ color: '#94a3b8' }}>-</span>}
                        </div>
                        {log.user?.email && (
                          <div style={{ fontSize: 9, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
                            {log.user.email}
                          </div>
                        )}
                      </td>
                      {/* Type */}
                      <td style={{ padding: '10px 14px' }}>
                        <LogTypeBadge type={log.type} />
                      </td>
                      {/* Amount */}
                      <td style={{ padding: '10px 14px', fontWeight: 800, color: amtColor, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>
                        {amt > 0 ? '+' : ''}{fmtDgcc(log.amount)}
                      </td>
                      {/* Balance After */}
                      <td style={{ padding: '10px 14px', color: '#0f172a', fontWeight: 700, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 11 }}>
                        {fmtDgcc(log.balance_after)} <span style={{ color: '#94a3b8', fontSize: 9 }}>DGCC</span>
                      </td>
                      {/* Description */}
                      <td style={{ padding: '10px 14px', color: '#475569', maxWidth: 260 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }}>
                          {log.description || <span style={{ color: '#94a3b8' }}>-</span>}
                        </div>
                      </td>
                      {/* Task / Reference */}
                      <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 9, color: '#94a3b8', maxWidth: 130 }}>
                        {log.task_id ? (
                          <span title={log.task_id} style={{ cursor: 'help' }}>{log.task_id.slice(0, 14)}…</span>
                        ) : '-'}
                      </td>
                    </tr>

                    {/* Expanded detail row */}
                    {isExpanded && (
                      <tr style={{ background: '#faf8ff', borderBottom: '2px solid rgba(124,58,237,0.1)' }}>
                        <td colSpan={8} style={{ padding: '0 14px 16px 48px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, paddingTop: 8 }}>
                            <div>
                              <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Full Timestamp (UTC)</div>
                              <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#0f172a', fontWeight: 600 }}>{dt.full}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>User ID</div>
                              <div style={{ fontSize: 10, fontFamily: 'monospace', color: '#475569', wordBreak: 'break-all' }}>{log.user_id || '-'}</div>
                            </div>
                            {log.task_id && (
                              <div>
                                <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Task / Transaction ID</div>
                                <div style={{ fontSize: 10, fontFamily: 'monospace', color: '#475569', wordBreak: 'break-all' }}>{log.task_id}</div>
                              </div>
                            )}
                            <div>
                              <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Log Record ID</div>
                              <div style={{ fontSize: 10, fontFamily: 'monospace', color: '#475569', wordBreak: 'break-all' }}>{log.id}</div>
                            </div>
                             <div>
                              <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Full Description</div>
                              <div style={{ fontSize: 11, color: '#0f172a' }}>{log.description || '-'}</div>
                            </div>
                          </div>

                          {log.type === 'deduction' && (
                            <div style={{ background: 'rgba(124,58,237,0.04)', border: '1px solid rgba(124,58,237,0.1)', borderRadius: 10, padding: '12px 16px', marginTop: 14 }}>
                              <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>📊</span> Cost & Markup Breakdown
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
                                <div>
                                  <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>Actual API Cost (Aggregator)</div>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', fontFamily: 'monospace' }}>
                                    {log.aggregator_cost_usd && parseFloat(log.aggregator_cost_usd) > 0 ? (
                                      `$${parseFloat(log.aggregator_cost_usd).toFixed(6)} USD`
                                    ) : (
                                      `$${(Math.abs(parseFloat(log.amount || 0)) / 1.80).toFixed(6)} USD (Estimated)`
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>Platform Markup (80%)</div>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed', fontFamily: 'monospace' }}>
                                    {log.aggregator_cost_usd && parseFloat(log.aggregator_cost_usd) > 0 ? (
                                      `+$${(Math.abs(parseFloat(log.amount || 0)) - parseFloat(log.aggregator_cost_usd)).toFixed(6)} USD (+80.00%)`
                                    ) : (
                                      `+$${(Math.abs(parseFloat(log.amount || 0)) - (Math.abs(parseFloat(log.amount || 0)) / 1.80)).toFixed(6)} USD (+80.00%)`
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 9, color: '#7c3aed', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>Final Deduction (Charge)</div>
                                  <div style={{ fontSize: 13, fontWeight: 900, color: '#dc2626', fontFamily: 'monospace' }}>
                                    {Math.abs(parseFloat(log.amount || 0)).toFixed(6)} DGCC
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ─────────────────────────────────────────────────── */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(124,58,237,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
            Showing {Math.min((page - 1) * 50 + 1, total)}–{Math.min(page * 50, total)} of {total.toLocaleString()} entries
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button disabled={page <= 1 || loading} onClick={() => setPage(1)}
              style={{ background: nm.bg, border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 700, color: page <= 1 ? '#cbd5e1' : '#7c3aed', cursor: page <= 1 ? 'default' : 'pointer', boxShadow: page > 1 ? nm.shadowSm : 'none' }}>
              «
            </button>
            <button disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}
              style={{ background: nm.bg, border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 11, fontWeight: 700, color: page <= 1 ? '#cbd5e1' : '#7c3aed', cursor: page <= 1 ? 'default' : 'pointer', boxShadow: page > 1 ? nm.shadowSm : 'none' }}>
              ‹ Prev
            </button>
            <span style={{ padding: '6px 16px', fontSize: 11, fontWeight: 800, color: '#7c3aed', background: '#f5f3ff', borderRadius: 8, border: '1px solid #ede9fe' }}>
              {page} / {totalPages || 1}
            </span>
            <button disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}
              style={{ background: nm.bg, border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 11, fontWeight: 700, color: page >= totalPages ? '#cbd5e1' : '#7c3aed', cursor: page >= totalPages ? 'default' : 'pointer', boxShadow: page < totalPages ? nm.shadowSm : 'none' }}>
              Next ›
            </button>
            <button disabled={page >= totalPages || loading} onClick={() => setPage(totalPages)}
              style={{ background: nm.bg, border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 700, color: page >= totalPages ? '#cbd5e1' : '#7c3aed', cursor: page >= totalPages ? 'default' : 'pointer', boxShadow: page < totalPages ? nm.shadowSm : 'none' }}>
              »
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(0.85); }
        }
      `}</style>
    </div>
  );
}
